import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getHospital, getHospitalHistory } from '../services/hospitalService';
import type { Hospital } from '../types';
import type { HospitalHistoryEntry } from '../services/hospitalService';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
}

function getChangeTypeBadgeClass(changeType: string): string {
  switch (changeType) {
    case 'CREATED':
      return 'change-badge change-badge-created';
    case 'UPDATED':
      return 'change-badge change-badge-updated';
    case 'DEACTIVATED':
      return 'change-badge change-badge-deactivated';
    default:
      return 'change-badge';
  }
}

function isFieldChanged(
  entry: HospitalHistoryEntry,
  field: 'hospital_name' | 'hospital_code' | 'is_active'
): boolean {
  if (!entry.previousValues) return false;
  const prev = entry.previousValues[field];
  if (prev === undefined) return false;
  switch (field) {
    case 'hospital_name':
      return prev !== entry.hospitalName;
    case 'hospital_code':
      return prev !== entry.hospitalCode;
    case 'is_active':
      return prev !== entry.isActive;
    default:
      return false;
  }
}

export default function HospitalHistoryPage() {
  const { hospitalUuid } = useParams<{ hospitalUuid: string }>();
  const navigate = useNavigate();

  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [history, setHistory] = useState<HospitalHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hospitalUuid) return;

    async function fetchData() {
      setIsLoading(true);
      setError(null);
      try {
        const [hospitalData, historyData] = await Promise.all([
          getHospital(hospitalUuid!),
          getHospitalHistory(hospitalUuid!),
        ]);
        setHospital(hospitalData);
        setHistory(historyData);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load hospital history';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [hospitalUuid]);

  return (
    <div className="hospital-history-page">
      <div className="page-header">
        <h2>{hospital ? `History: ${hospital.hospitalName}` : 'Hospital History'}</h2>
        <Button variant="secondary" onClick={() => navigate('/hospitals')}>
          Back to Hospitals
        </Button>
      </div>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <LoadingSpinner message="Loading history..." />
      ) : history.length === 0 ? (
        <p className="history-empty">No history entries found.</p>
      ) : (
        <table className="data-table" role="table">
          <thead>
            <tr>
              <th scope="col">Change Type</th>
              <th scope="col">Hospital Name</th>
              <th scope="col">Hospital Code</th>
              <th scope="col">Active</th>
              <th scope="col">Changed By</th>
              <th scope="col">Changed At</th>
            </tr>
          </thead>
          <tbody>
            {history.map((entry) => (
              <tr key={entry.historyId}>
                <td>
                  <span className={getChangeTypeBadgeClass(entry.changeType)}>
                    {entry.changeType}
                  </span>
                </td>
                <td className={isFieldChanged(entry, 'hospital_name') ? 'field-changed' : ''}>
                  {entry.hospitalName}
                  {isFieldChanged(entry, 'hospital_name') && (
                    <span className="prev-value">
                      was: {String(entry.previousValues?.hospital_name ?? '')}
                    </span>
                  )}
                </td>
                <td className={isFieldChanged(entry, 'hospital_code') ? 'field-changed' : ''}>
                  {entry.hospitalCode}
                  {isFieldChanged(entry, 'hospital_code') && (
                    <span className="prev-value">
                      was: {String(entry.previousValues?.hospital_code ?? '')}
                    </span>
                  )}
                </td>
                <td className={isFieldChanged(entry, 'is_active') ? 'field-changed' : ''}>
                  <span
                    className={`status-badge ${entry.isActive ? 'status-active' : 'status-inactive'}`}
                  >
                    {entry.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {isFieldChanged(entry, 'is_active') && (
                    <span className="prev-value">
                      was: {entry.previousValues?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </td>
                <td>{entry.changedBy}</td>
                <td>{formatDate(entry.changedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
